import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useRouter } from '@boss/hooks';

import { fetchAccountPriceInfoByProductId } from '../../client-queries/product/connector';
import { isB2b } from '../../utils';
import { useProfile } from '../use-profile/use-profile';

export const useBatchProductAccountPrices = (productIds: string[]) => {
  const { locale } = useRouter();
  const { isLoggedIn } = useProfile();
  const _isLoggedIn = isB2b ? true : isLoggedIn;

  const { data: batchPriceData, isFetching } = useQuery({
    queryKey: ['batchAccountPrices', locale, productIds.sort().join(','), _isLoggedIn],
    queryFn: async () => {
      const pricePromises = productIds.map(productId =>
        fetchAccountPriceInfoByProductId(productId, locale, _isLoggedIn)
          .then(data => ({ productId, data }))
          .catch(() => ({ productId, data: null })),
      );

      return Promise.all(pricePromises);
    },
    enabled: productIds.length > 0 && (isB2b ? isLoggedIn : true),
  });

  const priceMap = useMemo(() => {
    if (!batchPriceData) {
      return {};
    }

    return batchPriceData.reduce((map, { productId, data }) => {
      if (data?.prices) {
        const { fromPriceAmount, fromPriceAmountNet } = data.prices;

        map[productId] = fromPriceAmountNet || fromPriceAmount;
      }
      return map;
    }, {} as Record<string, number | undefined>);
  }, [batchPriceData]);

  return { priceMap, isLoading: isFetching };
};
